/**
 * Approval-rules V2 API client (Autonomy×Approval decision layer, migrations
 * 0084/0085). Backs the ONE account-level approval-rules MATRIX behind
 * VITE_APPROVAL_V2 — a plain-language rule sentence per row crossed with a
 * MODES checkbox group (HITL|HOTL|HOOL|Auto) and an ORGANIZATIONS checkbox
 * group (All-orgs + per-org).
 *
 * Mirrors the five sister publication clients (skills / connectors / templates /
 * permissions / roles-matrix) EXACTLY — the matrix read + the toggle endpoints
 * have the identical shape, so the UI reuses the same optimistic-toggle pattern:
 *
 *   GET    /api/account/approval-rules-base/all    → { rules, orgs } matrix
 *   POST   /api/account/approval-rules-base        → create a rule
 *   PATCH  /api/account/approval-rules-base/:id     → update a rule (incl. modes[])
 *   DELETE /api/account/approval-rules-base/:id     → delete a rule
 *   PUT    /api/account/approval-rules-base/:id/shares      → reconcile the set
 *   POST   /api/account/approval-rules-base/:id/shares      → tick ONE Workspace
 *   DELETE /api/account/approval-rules-base/:id/shares?orgId=<uuid> → untick ONE
 *   POST   /api/account/approval-rules-base/:id/shares/all  → SELECT ALL
 *   DELETE /api/account/approval-rules-base/:id/shares/all  → clear what it set
 *
 * THE LAST TWO ARE THE WHOLE POINT OF THIS FILE'S 2026-08-02 CHANGE. Migration
 * 0168 (`3e50f6d`) retired the all-orgs SENTINEL on all six publication tables
 * and rewrote this surface's gateway with it; it updated the other five clients
 * and not this one, so between that bake and now the "All organizations" box in
 * Automation rules read a field the wire no longer carried and wrote to routes
 * the gateway answers 400 to by name. See `ApprovalRuleShares` below.
 *
 * All endpoints are account-owner gated server-side (403 otherwise — the matrix
 * degrades gracefully on a 403, mirroring the credentials matrix). The WRITE
 * paths are additionally gated by the gateway ALL_ORGS_SETTINGS_ENABLED flag
 * (403 'all_orgs_settings_disabled' when off).
 *
 * Enums kept in lockstep with the gateway (all-orgs-settings.ts) + the DB
 * CHECK constraints (migration 0084) + apps/chaos-gateway/src/approval-actions.ts.
 */
import { apiJson } from './client';
import { normalizeList, normalizeOrgPublications } from './org-publication-shape';

// The new ACTION taxonomy (WHAT). NULL on the wire = the 'any' catch-all; the
// gateway also accepts the literal 'any' string. The client always sends an
// explicit value (mapping 'any' → null happens at the call site).
export type ApprovalAction =
  | 'any'
  | 'send_message'
  | 'post_public'
  | 'spend'
  | 'deploy'
  | 'delete'
  | 'run_command'
  | 'call_api'
  | 'use_credential'
  | 'web_search'
  | 'file_io';

// WHO — the actor class a rule applies to.
export type ActorKind = 'any' | 'role' | 'agent_instance';

// The three autonomy modes (stored uppercase). Legacy agent_only was merged
// machine value for the "Auto" UI label). A rule applies in the modes listed.
export type ApprovalMode = 'HITL' | 'HOTL' | 'HOOL'; // 3 modes only (founder 2026-06-27)

/**
 * WHERE a rule is in force, and which of those marks the "All organizations"
 * box placed. Structurally identical to `SkillPublications` and its four
 * siblings, which is why the shared seam can normalise all six.
 *
 *   orgIds       — every Workspace the rule applies in. THE list.
 *   viaAllOrgIds — the subset select-all placed (⊆ orgIds). The origin is
 *                  remembered because `DELETE /shares/all` removes only what it
 *                  put there; hand-placed marks survive, and the header box then
 *                  reads half-selected, which is honest.
 *
 * THERE IS NO `allOrgs` BOOLEAN, AND READING ONE IS THE DEFECT THIS FILE CARRIED
 * FROM `3e50f6d` TO 2026-08-02. "In force everywhere" used to be a STATUS — one
 * `org_id IS NULL` sentinel row — and because no read gate carried an account
 * filter it actually meant "in every Workspace that will ever exist, on any
 * account". Migration 0168 expanded each sentinel into one real row per
 * Workspace OF THIS ACCOUNT (production, checked 2026-08-02: 27 rows across 3
 * rules × the 7 live Workspaces + 2 archived, `granted_via` present, zero NULL
 * org_id). So "everywhere" is now simply the state where every Workspace is
 * ticked, which the UI counts off `orgIds` and therefore cannot disagree about.
 *
 * The cost of the release this file spent behind, measured rather than inferred:
 * the header box rendered UNTICKED on all three production rules while all
 * seven Workspaces were ticked, and clicking it called `POST /shares
 * {orgId:null}` / `DELETE /shares?orgId=all` — 400 from the gateway by name
 * ("use POST /shares/all…"). Both halves are gone; the proof that they are is
 * tests/browser-proof-settings/automation-rules-all-workspaces-proof.spec.ts.
 */
export interface ApprovalRuleShares {
  orgIds: string[];
  viaAllOrgIds: string[];
}

/**
 * What the gateway substitutes for a NULL `modes` column
 * (all-orgs-settings.ts:125) — the fallback here says the same thing rather
 * than inventing a narrower one, because a rule silently applying in FEWER
 * autonomy modes than it does is an approval gate quietly switching off.
 */
const WIRE_DEFAULT_MODES: readonly ApprovalMode[] = ['HITL', 'HOTL', 'HOOL'];

// One matrix column — an org belonging to the caller's account. Mirror
// CredentialMatrixOrg (square monogram/logo header cell).
export interface ApprovalMatrixOrg {
  id: string;
  displayName: string;
  logoUrl: string | null;
}

// One matrix row — a base rule with its decision-layer fields + the share set.
// Mirror CredentialMatrixRow. `action: null` = the 'any' catch-all.
export interface ApprovalRuleRow {
  id: string;
  accountId: string;
  trigger: string | null;
  decision: string;
  action: ApprovalAction | null;
  actorKind: ActorKind;
  actorRoleId: string | null;
  actorAgentId: string | null;
  modes: ApprovalMode[];
  scopeProjectId: string | null;
  scopeBoardId: string | null;
  scopeColumnId: string | null;
  scopeCardId: string | null;
  conditions: unknown;
  priority: number;
  active: boolean;
  createdBy: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  shares: ApprovalRuleShares;
}

export interface ApprovalRuleMatrix {
  rules: ApprovalRuleRow[];
  orgs: ApprovalMatrixOrg[];
}

// Create/update payloads — all the decision-layer fields the gateway accepts.
// `trigger` is OPTIONAL (action-only rules carry no lifecycle trigger, G1).
export interface CreateApprovalRuleV2Input {
  decision: string;
  trigger?: string | null;
  action?: ApprovalAction | null;
  actorKind?: ActorKind;
  actorRoleId?: string | null;
  actorAgentId?: string | null;
  modes?: ApprovalMode[];
  scopeProjectId?: string | null;
  scopeBoardId?: string | null;
  scopeColumnId?: string | null;
  scopeCardId?: string | null;
  conditions?: unknown;
  priority?: number;
  active?: boolean;
}
export type UpdateApprovalRuleV2Input = Partial<CreateApprovalRuleV2Input>;

interface SharesResp {
  shares: ApprovalRuleShares;
}

/**
 * The rule mutations answer `{ rule }`, not a bare rule — every one of them
 * ends `c.json({ rule: approvalRuleBaseRowToApi(...) })` (all-orgs-settings.ts
 * :565, :652). This client used to type those responses as `ApprovalRuleRow`
 * and hand back the envelope, which no call site happened to read; found while
 * sweeping the whole client against the whole gateway for this change, and
 * unwrapped here so the next reader is not the one who finds out.
 */
interface RuleResp {
  rule: ApprovalRuleRow;
}

const BASE = '/account/approval-rules-base';

export const approvalRulesV2Api = {
  // Account-wide matrix — every base rule (with modes[] + share set) crossed
  // with the account's orgs as columns. Account-owner only (403 otherwise).
  //
  // NORMALISED AT THE DOOR, through the SAME function as the other five
  // surfaces (./org-publication-shape) rather than a local copy — two copies of
  // one guard is two things to keep in step, which is the defect one level up.
  // Three lists the matrix reads DURING RENDER, so a non-array in any of them
  // takes the whole Automation-rules section into the settings error boundary:
  //
  //   shares.orgIds / shares.viaAllOrgIds — `cellState` and `allOrgsState`,
  //     both imported from the shared <CrossOrgMatrix> module.
  //   modes — `row.modes.includes(m)` / `row.modes.length`. The gateway
  //     defaults a NULL column to all three (all-orgs-settings.ts:125), and the
  //     same route also serves rows with no `shares` key at all
  //     (`...(shares ? { shares } : {})`, :137) — proof in this repository that
  //     "the field is always there" is a property of one code path, not of the
  //     endpoint.
  listAll(): Promise<ApprovalRuleMatrix> {
    return apiJson<ApprovalRuleMatrix>(`${BASE}/all`).then((m) => ({
      ...m,
      rules: m.rules.map((r) => ({
        ...r,
        modes: normalizeList<ApprovalMode>(r.modes, [...WIRE_DEFAULT_MODES]),
        shares: normalizeOrgPublications(r.shares),
      })),
    }));
  },
  // The gateway seeds a new rule with a select-all (one real mark per Workspace
  // of the account, all_workspaces origin) so it is in force immediately; the
  // matrix re-fetch is what puts that on screen.
  create(input: CreateApprovalRuleV2Input): Promise<ApprovalRuleRow> {
    return apiJson<RuleResp>(BASE, { method: 'POST', json: input }).then((r) => r.rule);
  },
  upsertRule(
    id: string | null,
    input: CreateApprovalRuleV2Input | UpdateApprovalRuleV2Input,
  ): Promise<ApprovalRuleRow> {
    return id === null
      ? this.create(input as CreateApprovalRuleV2Input)
      : this.update(id, input);
  },
  update(id: string, input: UpdateApprovalRuleV2Input): Promise<ApprovalRuleRow> {
    return apiJson<RuleResp>(`${BASE}/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      json: input,
    }).then((r) => r.rule);
  },
  // The MODES checkbox group persists via a plain rule PATCH (modes[] is a rule
  // column, not a share). Convenience wrapper so the matrix call site reads like
  // the share toggles.
  setModes(id: string, modes: ApprovalMode[]): Promise<ApprovalRuleRow> {
    return this.update(id, { modes });
  },
  // The DELETE answers `{ ok: true }` and nothing else — it used to be typed
  // here as carrying an `id` it has never sent (all-orgs-settings.ts:727).
  remove(id: string): Promise<{ ok: boolean }> {
    return apiJson<{ ok: boolean }>(`${BASE}/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  },
  // ── WORKSPACES this rule is in force in (mirror the five sisters) ──────
  // Reconcile the FULL set in one call. Body { orgIds } — 0168 dropped the
  // `allOrgs` half with the sentinel it used to write, and the gateway now
  // ignores anything else in the body. Reconciling to "everywhere" is
  // selectAllOrgs(), a different act: it records WHY each mark is there, and a
  // list the caller hand-assembled is by definition manual.
  setShares(id: string, shares: Pick<ApprovalRuleShares, 'orgIds'>): Promise<ApprovalRuleShares> {
    return apiJson<SharesResp>(`${BASE}/${encodeURIComponent(id)}/shares`, {
      method: 'PUT',
      json: { orgIds: shares.orgIds },
    }).then((r) => r.shares);
  },
  // Tick ONE Workspace. The mark is MANUAL. 0168: `orgId: null` used to mean the
  // sentinel and is now a 400 by name — selectAllOrgs() is the route for that.
  addShare(id: string, orgId: string): Promise<ApprovalRuleShares> {
    return apiJson<SharesResp>(`${BASE}/${encodeURIComponent(id)}/shares`, {
      method: 'POST',
      json: { orgId },
    }).then((r) => r.shares);
  },
  // Untick ONE Workspace. Works the same whether the mark was hand-placed or put
  // there by select-all — "after that, any one of them can be unticked on its
  // own" is the whole point of 0168.
  removeShare(id: string, orgId: string): Promise<ApprovalRuleShares> {
    return apiJson<SharesResp>(
      `${BASE}/${encodeURIComponent(id)}/shares?orgId=${encodeURIComponent(orgId)}`,
      { method: 'DELETE' },
    ).then((r) => r.shares);
  },
  // THE SELECT-ALL. Puts a separate `all_workspaces` mark on every Workspace of
  // the account; any one of them can then be unticked on its own.
  selectAllOrgs(id: string): Promise<ApprovalRuleShares> {
    return apiJson<SharesResp>(`${BASE}/${encodeURIComponent(id)}/shares/all`, {
      method: 'POST',
    }).then((r) => r.shares);
  },
  // The same box, unticked. IT REMOVES ONLY THE MARKS IT MADE — hand-placed ones
  // survive, which is why the header box can end up half-selected afterwards.
  clearAllOrgs(id: string): Promise<ApprovalRuleShares> {
    return apiJson<SharesResp>(`${BASE}/${encodeURIComponent(id)}/shares/all`, {
      method: 'DELETE',
    }).then((r) => r.shares);
  },
  // Flip one cell to the desired state (used by the matrix checkbox handler).
  // `orgId === null` is the HEADER box, which since 0168 is a real bulk write
  // rather than a status flag.
  toggleShare(id: string, orgId: string | null, on: boolean): Promise<ApprovalRuleShares> {
    if (orgId === null) return on ? this.selectAllOrgs(id) : this.clearAllOrgs(id);
    return on ? this.addShare(id, orgId) : this.removeShare(id, orgId);
  },
};

export const approvalRulesV2Keys = {
  all: ['approval-rules-v2'] as const,
  matrix: () => ['approval-rules-v2', 'matrix'] as const,
};
